import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import FilterBar from '../components/FilterBar';
import Panel from '../components/Panel';

import WeekNote from '../components/WeekNote';

function InsightCard({ label, title, body }) {
  return (
    <div style={{
      background: 'var(--card-bg)', border: '1px solid var(--card-border)',
      borderRadius: '24px', padding: '24px', boxShadow: 'var(--shadow-md)',
    }}>
      <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: '#8b7355', marginBottom: '12px' }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '32px', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-dark)', lineHeight: 1.3, marginBottom: '12px' }}>
        {title}
      </div>
      <p style={{ fontSize: '14px', lineHeight: 1.75, color: 'var(--text-light)', margin: 0 }}>{body}</p>
    </div>
  );
}

function KpiCard({ label, value, note, accent = 'blue' }) {
  const colors = { blue: '#1f5eff', green: '#1f7a3f', amber: '#c98b12', teal: '#0f766e', red: '#c2410c' };
  return (
    <div className="metric-card">
      <span className="metric-label">{label}</span>
      <div className="metric-value" style={{ color: colors[accent] || colors.blue }}>{value}</div>
      {note && <div style={{ fontSize: '12px', color: 'var(--text-lighter)', marginTop: '6px' }}>{note}</div>}
    </div>
  );
}

function pct(v) { return v == null ? '0%' : `${Math.round(Number(v) * 100)}%`; }

function FieldOps({ filters, loading, setLoading, onDrilldown }) {
  const [summary, setSummary] = useState({});
  const [trends, setTrends] = useState([]);
  const [behaviors, setBehaviors] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [emotionMap, setEmotionMap] = useState([]);
  const [scoreDist, setScoreDist] = useState([]);
  const [coaching, setCoaching] = useState([]);
  const [agents, setAgents] = useState([]);
  const [coachAgent, setCoachAgent] = useState('');

  const fetchIdRef = React.useRef(0);

  const fetchData = (selectedAgent) => {
    const id = ++fetchIdRef.current;
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') params.append(k, v); });
    if (selectedAgent) params.append('coach_agent', selectedAgent);

    axios.get(`/api/sales-performance?${params}`)
      .then(res => {
        if (id !== fetchIdRef.current) return;
        setSummary(res.data.summary || {});
        setTrends(res.data.trends || []);
        setBehaviors(res.data.behaviors || []);
        setLeaderboard(res.data.leaderboard || []);
        setEmotionMap(res.data.emotionMap || []);
        setScoreDist(res.data.scoreDistribution || []);
        setCoaching(res.data.coaching || []);
        setAgents(res.data.agents || []);
      })
      .catch(err => { if (id === fetchIdRef.current) console.error('Error loading Sales Performance:', err); })
      .finally(() => { if (id === fetchIdRef.current) setLoading(false); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(coachAgent); }, [filters]);

  const handleCoachSelect = (agent) => {
    setCoachAgent(agent);
    fetchData(agent);
  };

  const topAgent = leaderboard[0];
  const topBehavior = behaviors.sort((a, b) => Number(b.win_rate || 0) - Number(a.win_rate || 0))[0];
  const gapDimension = coaching.length ? coaching[0] : null;

  return (
    <div>
      {/* Header */}
      <div className="module-header">
        <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: 'var(--text-lighter)', marginBottom: '6px' }}>
          Team Analytics
        </div>
        <h2>Sales Performance</h2>
        <p>Team quality, win-driving behaviors, agent benchmarking, and coaching priorities.</p>
      </div>

      {/* Filter Bar */}
      <FilterBar />

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        <KpiCard label="Conversations" value={summary.total_conversations ?? 0} note="With agent behavior insights" accent="blue" />
        <KpiCard label="Avg Quality" value={pct(summary.avg_quality)} note="Overall agent score" accent="green" />
        <KpiCard label="Avg Empathy" value={pct(summary.avg_empathy)} note="Customer connection" accent="teal" />
        <KpiCard label="Avg Technical" value={pct(summary.avg_technical)} note="Knowledge & expertise" accent="amber" />
        <KpiCard label="Avg Closing" value={pct(summary.avg_closing)} note="Confidence & outcome" accent="red" />
      </div>

      {/* Insight Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        <InsightCard
          label="Top Agent"
          title={topAgent ? `${topAgent.agent_name} leads in quality.` : 'No agent data yet.'}
          body={topAgent
            ? `${topAgent.agent_name} is averaging ${pct(topAgent.avg_quality)} quality across ${topAgent.conversation_count} conversation${topAgent.conversation_count !== 1 ? 's' : ''}.`
            : 'Process more conversations to surface agent rankings.'}
        />
        <InsightCard
          label="Win Behavior"
          title={topBehavior ? `${topBehavior.behavior_name} drives the most wins.` : 'No behavior data yet.'}
          body={topBehavior
            ? `${topBehavior.behavior_name} correlates with a ${pct(topBehavior.win_rate)} win rate — the highest among detected behaviors in this scope.`
            : 'Increase conversation volume to identify win-driving behaviors.'}
        />
        <InsightCard
          label="Coaching Focus"
          title={gapDimension ? `${gapDimension.dimension} has the widest gap from team benchmark.` : 'Select an agent to see coaching focus.'}
          body={gapDimension
            ? `Agent ${coachAgent || 'selected'} scores ${pct(gapDimension.agent_score)} vs team ${pct(gapDimension.team_score)} on ${gapDimension.dimension} — a gap of ${(Number(gapDimension.gap) * 100).toFixed(1)} percentage points.`
            : 'Use the coaching section below to select an agent and view dimension-level gaps.'}
        />
      </div>

      {/* Performance Trend + Behavior Win Rate */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
        <Panel loading={loading} title="Performance Trend" subtitle="Weekly quality and closing scores">
          {trends.length ? (
            <>
              <Plot
                data={[
                  {
                    x: trends.map(t => t.week_start),
                    y: trends.map(t => t.avg_quality || 0),
                    name: 'Quality', type: 'scatter', mode: 'lines+markers',
                    line: { color: '#1f5eff', width: 2.5 },
                  },
                  {
                    x: trends.map(t => t.week_start),
                    y: trends.map(t => t.avg_closing || 0),
                    name: 'Closing', type: 'scatter', mode: 'lines+markers',
                    line: { color: '#c98b12', width: 2.5 },
                  },
                ]}
                layout={{
                  xaxis: { title: 'Week starting' },
                  yaxis: { title: 'Score', tickformat: '.0%' },
                  plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--card-bg)',
                  margin: { t: 20, r: 20, b: 50, l: 60 }, height: 280,
                  hovermode: 'x unified', legend: { orientation: 'h', y: -0.25 },
                }}
                config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'] }}
                style={{ width: '100%' }}
              />
              <WeekNote />
            </>
          ) : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No data available</div>}
        </Panel>

        <Panel loading={loading} title="Behavior Win-Rate" subtitle="Which behaviors correlate with wins">
          {behaviors.length ? (
            <Plot
              data={[{
                x: behaviors.map(b => b.win_rate || 0),
                y: behaviors.map(b => b.behavior_name),
                type: 'bar', orientation: 'h',
                marker: { color: '#1f7a3f' },
              }]}
              layout={{
                xaxis: { title: 'Win Rate', tickformat: '.0%' },
                plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--card-bg)',
                margin: { t: 20, r: 20, b: 40, l: 160 }, height: 280,
              }}
              config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'] }}
              style={{ width: '100%' }}
            />
          ) : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No behavior data available</div>}
        </Panel>
      </div>

      {/* Emotion Map + Score Distribution */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
        <Panel loading={loading} title="Team Emotion Map" subtitle="Empathy vs closing confidence per agent">
          {emotionMap.length ? (
            <Plot
              data={[{
                x: emotionMap.map(a => a.avg_empathy || 0),
                y: emotionMap.map(a => a.avg_closing || 0),
                text: emotionMap.map(a => a.agent_name),
                mode: 'markers+text',
                type: 'scatter',
                textposition: 'top center',
                marker: {
                  size: emotionMap.map(a => Math.max(8, Math.min(30, (a.conversation_count || 1) * 2))),
                  color: '#1f5eff', opacity: 0.7,
                },
              }]}
              layout={{
                xaxis: { title: 'Avg Empathy', range: [0, 1] },
                yaxis: { title: 'Avg Closing', range: [0, 1] },
                plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--card-bg)',
                margin: { t: 20, r: 20, b: 50, l: 60 }, height: 280,
                showlegend: false,
              }}
              config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'] }}
              style={{ width: '100%' }}
            />
          ) : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No data available</div>}
        </Panel>

        <Panel loading={loading} title="Closing Score Distribution" subtitle="Per-agent min / avg / max closing confidence">
          {scoreDist.length ? (
            <Plot
              data={[
                {
                  x: scoreDist.map(a => a.avg_closing || 0),
                  y: scoreDist.map(a => a.agent_name),
                  error_x: {
                    type: 'data',
                    symmetric: false,
                    array: scoreDist.map(a => (a.max_closing || 0) - (a.avg_closing || 0)),
                    arrayminus: scoreDist.map(a => (a.avg_closing || 0) - (a.min_closing || 0)),
                    color: '#c98b12', thickness: 2,
                  },
                  type: 'scatter', mode: 'markers',
                  marker: { color: '#1f5eff', size: 10 },
                },
              ]}
              layout={{
                xaxis: { title: 'Closing Score', range: [0, 1] },
                plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--card-bg)',
                margin: { t: 20, r: 20, b: 40, l: 140 }, height: 280,
                showlegend: false,
              }}
              config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'] }}
              style={{ width: '100%' }}
            />
          ) : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No data available</div>}
        </Panel>
      </div>

      {/* Coaching Section */}
      <Panel loading={loading} title="Coaching Center" subtitle="Select an agent to view dimension-level gap vs team benchmark">
        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-dark)', marginRight: '12px' }}>Agent:</label>
          <select
            value={coachAgent}
            onChange={e => handleCoachSelect(e.target.value)}
            style={{
              padding: '8px 12px', border: '1px solid var(--card-border)', borderRadius: '10px',
              background: 'var(--card-bg)', color: 'var(--text-dark)', fontSize: '14px',
            }}
          >
            <option value="">— select agent —</option>
            {agents.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>

        {coaching.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <Plot
              data={[
                {
                  type: 'scatterpolar',
                  r: coaching.map(c => Math.max(0, c.agent_score || 0)),
                  theta: coaching.map(c => c.dimension),
                  fill: 'toself',
                  name: coachAgent,
                  line: { color: '#1f5eff' },
                  fillcolor: 'rgba(31,94,255,0.15)',
                },
                {
                  type: 'scatterpolar',
                  r: coaching.map(c => Math.max(0, c.team_score || 0)),
                  theta: coaching.map(c => c.dimension),
                  fill: 'toself',
                  name: 'Team Avg',
                  line: { color: '#1f7a3f' },
                  fillcolor: 'rgba(31,122,63,0.1)',
                },
              ]}
              layout={{
                polar: { radialaxis: { visible: true, range: [0, 1] } },
                paper_bgcolor: 'var(--card-bg)', margin: { t: 20, r: 40, b: 20, l: 40 }, height: 280,
                legend: { orientation: 'h', y: -0.15 },
              }}
              config={{ responsive: true, displaylogo: false }}
              style={{ width: '100%' }}
            />
            <div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '12px' }}>Gap Analysis</div>
              {coaching.map(c => (
                <div key={c.dimension} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--card-border)' }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-dark)', fontWeight: 500 }}>{c.dimension}</span>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-light)' }}>Agent: {pct(c.agent_score)}</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-light)' }}>Team: {pct(c.team_score)}</span>
                    <span style={{
                      fontSize: '12px', fontWeight: 700, padding: '2px 8px', borderRadius: '8px',
                      background: (c.gap || 0) >= 0 ? 'rgba(31,122,63,0.1)' : 'rgba(194,65,12,0.1)',
                      color: (c.gap || 0) >= 0 ? '#1f7a3f' : '#c2410c',
                    }}>
                      {(c.gap || 0) >= 0 ? '+' : ''}{(Number(c.gap || 0) * 100).toFixed(1)}pp
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-lighter)', fontSize: '14px' }}>
            {coachAgent ? 'No coaching data available for this agent.' : 'Select an agent above to view coaching analysis.'}
          </div>
        )}
      </Panel>

      {/* Agent Leaderboard */}
      <div style={{ marginTop: '32px' }}>
        <Panel loading={loading} title="Agent Leaderboard" subtitle="Ranked by average quality score">
          <table className="data-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Conversations</th>
                <th>Quality</th>
                <th>Empathy</th>
                <th>Technical</th>
                <th>Closing</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.length ? leaderboard.map((agent, i) => (
                <tr
                  key={agent.agent_name}
                  className={onDrilldown ? 'clickable-row' : ''}
                  onClick={onDrilldown ? () => onDrilldown('conversations', agent.agent_name, { title: `Conversations: ${agent.agent_name}` }) : undefined}
                >
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {i < 3 && <span style={{ fontSize: '16px' }}>{['🥇', '🥈', '🥉'][i]}</span>}
                      <strong>{agent.agent_name}</strong>
                    </div>
                  </td>
                  <td>{agent.conversation_count}</td>
                  <td><span className="badge success">{pct(agent.avg_quality)}</span></td>
                  <td><span className="badge info">{pct(agent.avg_empathy)}</span></td>
                  <td><span className="badge info">{pct(agent.avg_technical)}</span></td>
                  <td><span className="badge info">{pct(agent.avg_closing)}</span></td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', color: 'var(--text-lighter)' }}>No agent data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </Panel>
      </div>
    </div>
  );
}

export default FieldOps;
